/**
 * GenerateUserProfileShareDataUseCase
 *
 * Computes share title, text, URL and downloads the profile image
 * for use with both the native Web Share API and the social share sheet.
 */

import { env } from '@/config/env';
import { remoteConfigService } from '@/domain/services/remoteConfig/remoteConfigService';
import { UserUtils } from '@/domain/utils/userUtils';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import { downloadImageUseCase } from '../storage/downloadImageUseCase';
import { canShareFilesUseCase } from './canShareFilesUseCase';
import type { ShareContent } from './shareContent';

class GenerateUserProfileShareDataUseCase {
  async execute(user: UserEntity): Promise<ShareContent> {
    const shareUrl = `${env.DOMAIN_NAME}/${user.shareLink}`;
    const title = `Expause - ${user.firstName} ${user.lastName}`;
    const maxChars = remoteConfigService.shareUserMaxCharacters;
    const bio = user.bio?.trim() ?? '';
    const text = bio ? bio.substring(0, Math.min(bio.length, maxChars)) : '';

    const profileUrl = UserUtils.generateUserProfileImageUrl(user.firebaseId);
    const downloadedFile = await downloadImageUseCase.execute(profileUrl, 'profile.jpg');
    const file = downloadedFile && canShareFilesUseCase.execute([downloadedFile]) ? downloadedFile : null;

    return { title, text, shareUrl, file };
  }
}

export const generateUserProfileShareDataUseCase = new GenerateUserProfileShareDataUseCase();
